import _extends from "@babel/runtime/helpers/extends";
/* eslint-disable @repo/internal/react/consistent-props-definitions */
import React from 'react';
import warnOnce from '@atlaskit/ds-lib/warn-once';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { N500 } from '@atlaskit/theme/colors';
import { overrideStyleFunction } from '../../common/styles';
import { Container } from '../Header';
import { CustomItem } from '../Item';
const iconContainerStyles = xcss({
  display: 'inline-block',
  width: '1.5rem',
  height: '1.5rem'
});
/**
 * __Footer__
 *
 * - [Examples](https://atlassian.design/components/side-navigation/examples#header-and-footer)
 * - [Code](https://atlassian.design/components/side-navigation/code)
 */
const OldFooter = props => {
  const cssFn = overrideStyleFunction(() => ({
    userSelect: 'auto',
    display: 'block',
    textAlign: 'center',
    minHeight: '24px',
    alignItems: 'center',
    width: '100%',
    '[data-item-elem-before]': {
      marginRight: "var(--ds-space-0, 0px)",
      marginBottom: "var(--ds-space-100, 8px)",
      display: 'inline-block'
    },
    '[data-item-title]': {
      textAlign: 'center',
      font: "var(--ds-font-body-UNSAFE_small, normal 400 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, system-ui, \"Helvetica Neue\", sans-serif)"
    },
    '[data-item-description]': {
      textAlign: 'center',
      display: 'inline-block',
      margin: "var(--ds-space-075, 6px)"
    },
    // Will look interactive if the `component` is anything other than a div.
    'div&:hover': {
      backgroundColor: "var(--ds-background-neutral-subtle, transparent)",
      cursor: 'default'
    },
    'div&:active': {
      backgroundColor: "var(--ds-background-neutral-subtle, transparent)",
      color: `var(--ds-text-subtle, ${N500})`
    }
  }), props.cssFn);

  // https://stackoverflow.com/a/39333479
  const safeProps = (({
    iconBefore,
    onClick,
    description,
    children,
    testId
  }) => ({
    iconBefore,
    onClick,
    description,
    children,
    testId
  }))(props);
  return /*#__PURE__*/React.createElement(CustomItem, _extends({}, safeProps, {
    component: props.component || Container
    // eslint-disable-next-line @repo/internal/react/no-unsafe-overrides
    ,
    cssFn: cssFn
  }));
};

/**
 * __Footer__
 *
 * - [Examples](https://atlassian.design/components/side-navigation/examples#header-and-footer)
 * - [Code](https://atlassian.design/components/side-navigation/code)
 */
const Footer = ({
  description,
  children,
  iconBefore,
  testId
}) => {
  return /*#__PURE__*/React.createElement(Box, {
    padding: "space.100",
    testId: testId
  }, /*#__PURE__*/React.createElement(Stack, {
    space: "space.100",
    alignInline: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: iconContainerStyles
  }, iconBefore), /*#__PURE__*/React.createElement(Stack, {
    space: "space.075"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "UNSAFE_small",
    align: "center",
    color: "inherit"
  }, children), description && /*#__PURE__*/React.createElement(Text, {
    size: "UNSAFE_small",
    as: "p",
    align: "center",
    color: "color.text.subtlest"
  }, description))));
};

/**
 * __Footer__
 *
 * - [Examples](https://atlassian.design/components/side-navigation/examples#header-and-footer)
 * - [Code](https://atlassian.design/components/side-navigation/code)
 */
const FooterFacade = ({
  useDeprecatedApi = true,
  description,
  iconBefore,
  testId,
  children,
  component,
  cssFn,
  onClick
}) => {
  if (!useDeprecatedApi) {
    return /*#__PURE__*/React.createElement(Footer, {
      iconBefore: iconBefore,
      description: description,
      testId: testId
    }, children);
  }
  if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'CI') {
    warnOnce('The `@atlaskit/side-navigation` Footer has simplified its API, removing support for the `component`, `cssFn` and `onClick` props.\n\nThese props will stop working in a future major release. Reach out to #help-design-system on slack for help.');
  }
  return /*#__PURE__*/React.createElement(OldFooter, {
    onClick: onClick,
    component: component
    // eslint-disable-next-line @repo/internal/react/no-unsafe-overrides
    ,
    cssFn: cssFn,
    iconBefore: iconBefore,
    description: description,
    testId: testId
  }, children);
};
export default FooterFacade;